/**
 * Browser-local registry of in-flight bulk-add jobs.
 *
 * The persisted shape outlives F5 / closed-and-reopened-tab so the
 * App-shell poller resumes polling whatever was in flight when the page
 * was last loaded. Entries older than `BUDGET_MS` are filtered out on
 * read — they were either dispatched too long ago for the backend's
 * Redis TTL to still have the result, or the operator gave up. We
 * treat the localStorage value as best-effort cache: any malformed
 * payload is treated as empty and overwritten on next write.
 */

const STORAGE_KEY = 'yogi:bulk-add:in-flight'
const BUDGET_MS = 5 * 60 * 1000
// Custom DOM event the poller listens for so SAME-TAB writes are seen.
// `storage` events only fire across OTHER tabs — without this bridge,
// a submit-then-poll loop on one tab would never reconcile.
const CHANGE_EVENT = 'yogi:bulk-add:in-flight-change'

export type InFlightJob = {
  jobId: string
  hubUuid: string
  hubTitle: string
  startedAt: number
}

export const BULK_ADD_JOB_STORAGE_KEY = STORAGE_KEY
export const BULK_ADD_JOB_BUDGET_MS = BUDGET_MS
export const BULK_ADD_JOB_CHANGE_EVENT = CHANGE_EVENT

/** Return the current in-flight jobs, filtering out expired entries. */
export function readInFlightJobs(now: number = Date.now()): InFlightJob[] {
  const raw = safeReadRaw()
  if (raw === null) return []
  return raw.filter((job) => now - job.startedAt < BUDGET_MS)
}

/** Append a new in-flight job; quietly merges with whatever is stored. */
export function addInFlightJob(job: InFlightJob): void {
  const current = safeReadRaw() ?? []
  const next = [...current.filter((j) => j.jobId !== job.jobId), job]
  writeRaw(next)
  notifyChange()
}

/** Remove an in-flight job by id (no-op if absent). */
export function removeInFlightJob(jobId: string): void {
  const current = safeReadRaw()
  if (current === null) return
  const next = current.filter((j) => j.jobId !== jobId)
  writeRaw(next)
  notifyChange()
}

/** Test helper: clear all in-flight entries. */
export function clearInFlightJobs(): void {
  writeRaw([])
  notifyChange()
}

function notifyChange(): void {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // Non-DOM environments (some test runners): silently drop. The poller
    // either re-reads on its next storage event or stays idle.
  }
}

function safeReadRaw(): InFlightJob[] | null {
  const text = readStorage(STORAGE_KEY)
  if (text === null) return null
  try {
    const parsed = JSON.parse(text)
    if (!Array.isArray(parsed)) return null
    return parsed.filter(isInFlightJob)
  } catch {
    return null
  }
}

function writeRaw(jobs: InFlightJob[]): void {
  writeStorage(STORAGE_KEY, JSON.stringify(jobs))
}

function isInFlightJob(value: unknown): value is InFlightJob {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v['jobId'] === 'string' &&
    typeof v['hubUuid'] === 'string' &&
    typeof v['hubTitle'] === 'string' &&
    typeof v['startedAt'] === 'number'
  )
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Private-mode browsers / quota-exceeded: silently drop. The poller
    // will simply not resume after a reload, but no crash.
  }
}
